/**
 * Responsibility: pull the ground toward rail level near the track, and leave it alone everywhere else.
 *
 * ## This is the one place in the world where terrain yields to something built on it
 *
 * Everywhere else the rule is absolute: structures sample terrain and never the reverse. The airfield states
 * why it cannot be broken from inside a biome — blended elevation is a weighted sum, so a pad at 70% biome
 * weight comes out 70% flat plus 30% of whatever the neighbour is doing, and the runway tilts anyway. Which
 * is why a runway sits on an embankment and the ground under it is untouched.
 *
 * A corridor applied **after** the blend has no such problem, and that is the whole reason it works. It is
 * also the only way to get a cutting: an embankment can be drawn as structure above the ground, but you
 * cannot draw away ground that is already there.
 *
 * ## The shape is a river valley
 *
 * `carveHollow` already does exactly this — hold a level out to a rim, then ease back to the surrounding land
 * with an exponent that decides whether the result has a flat bottom or a funnel. A cutting is that hollow
 * with a railway in it, and an embankment is the same expression with the level above the land instead of
 * below it. So this module is a query and a call, and the arithmetic has one home.
 *
 * ## Why it is invisible on flat country
 *
 * The shaped level and the natural ground agree there, so the corridor does nothing and draws nothing. It
 * exists logically along the entire route and shows up only where it had to cut or fill — which is what makes
 * a corridor across farmland read as track lying on the ground, with no visible earthworks at all.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { carveHollow } from '../world/hollow.js';
import { RAILWAY } from '../config.js';

/**
 * Build the corridor deformation for a surveyed route.
 *
 * @param {object} options
 * @param {{nearest: Function}} options.surveyor - From `createSurveyor`. Must already be walked past the
 *   ground being asked about; see the note on `nearest`.
 * @param {object} [options.tuning] - Overrides for the `RAILWAY` config block.
 * @returns {{deform: (x: number, z: number, elevation: number) => number, earthworksAt: Function}}
 */
export function createCorridor({ surveyor, profile = null, tuning: overrides = RAILWAY }) {
  const tuning = overrides === RAILWAY ? RAILWAY : { ...RAILWAY, ...overrides };

  if (!surveyor || typeof surveyor.nearest !== 'function') {
    throw new Error('createCorridor: surveyor must expose nearest');
  }
  if (!(tuning.CORRIDOR_REACH > tuning.FORMATION_HALF_WIDTH)) {
    throw new Error('createCorridor: CORRIDOR_REACH must exceed FORMATION_HALF_WIDTH');
  }

  const formation = tuning.FORMATION_HALF_WIDTH;

  /*
    Widest the corridor could be anywhere, used only to reject a coordinate before paying for a biome sample.
    Most of the world is nowhere near the line and must cost as little as possible.
  */
  const outerReach = profile ? profile.maxReach : tuning.CORRIDOR_REACH;

  /** Corridor shape here: from the profile when there is one, otherwise the single default. */
  function shapeAt(x, z) {
    if (profile === null) {
      return { reach: tuning.CORRIDOR_REACH, batter: tuning.CORRIDOR_BATTER, step: 0 };
    }
    return profile.shapeAt(x, z);
  }

  return {
    /**
     * Ground elevation with the corridor applied.
     *
     * Shaped to `elevation` exactly at the corridor's edge, so it meets untouched terrain continuously with no
     * blending code — the same property `carveHollow` gives a river bank, and for the same reason.
     *
     * @param {number} x
     * @param {number} z
     * @param {number} elevation - Naturally blended elevation at that coordinate.
     * @returns {number}
     */
    deform(x, z, elevation) {
      const track = surveyor.nearest(x, z);
      if (track === null || track.lateral >= outerReach) return elevation;

      const shape = shapeAt(x, z);
      if (track.lateral >= shape.reach) return elevation;

      /*
        Held dead level across the formation, so the trackbed is flat rather than merely gentler. Everything
        outside it eases back to the land — 0 at the formation edge, 1 where the corridor ends.
      */
      const fromRim =
        track.lateral <= formation ? 0 : (track.lateral - formation) / (shape.reach - formation);

      const smooth = carveHollow(elevation, track.height, fromRim, shape.batter);
      if (shape.step <= 0) return smooth;

      /*
        Benched, where the country is rock. A smooth wall reads as a moulded trench and a stepped one reads as
        rock — the same call `canyon.js` makes about its own gorges, using the same step height.

        **Faded out toward the rim, and that is not cosmetic.** Quantising is a discontinuous operation, so
        applying it right up to the corridor's edge would snap the last value away from the natural ground and
        leave a visible ring around the whole route. Weighting it by how far *inside* the corridor we are keeps
        the join exact, because at the rim the stepping contributes nothing.
      */
      const above = smooth - track.height;
      const stepped = track.height + Math.round(above / shape.step) * shape.step;

      return smooth + (stepped - smooth) * (1 - fromRim);
    },

    /**
     * How much cut or fill the corridor needs at a coordinate, in world units.
     *
     * Positive is fill, negative is cut. **For measurement, not for generation.** Everything about whether
     * this design is affordable is a question about the distribution of this number — measured over three
     * routes, about 95% of a route needs less than 150 units of it — so it has to be reportable rather than
     * inferred from the shaped output.
     *
     * @returns {number|null} Null away from the line.
     */
    earthworksAt(x, z, elevation) {
      const track = surveyor.nearest(x, z);
      if (track === null || track.lateral >= outerReach) return null;

      return track.height - elevation;
    },
  };
}
